/**
 * The Model Viewer's ships and window, as a `SelectionScope`.
 *
 * Every model - Service Power, SFOC, whatever comes next - reads this one
 * selection, so switching model keeps what you picked, and the sidebar's Query
 * card is the same control in the same place whichever model is showing. It is
 * not Explore's: a model is a different question from the chart on the Plot tab,
 * so picking a ship here never moves the ship Explore is plotting. Same seam,
 * same reason, as a detached dashboard's `dashboardSelection`.
 *
 * It does start from Explore's. The first time the Model Viewer is open with
 * nothing picked, it takes Explore's ships and window (`seedFromExplore`), so
 * arriving from a plot of the Aurora shows the Aurora's models.
 */

import type { SelectionScope } from './selection-scope.ts';
import {
  activePreset,
  activeTab,
  endDate,
  MAX_PLOT_SHIPS,
  modelEnd,
  modelPreset,
  modelShips,
  modelStart,
  plottedShipIds,
  selectedShip,
  startDate,
  type DatePreset,
} from '../../state/store.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import { presetWindow, shiftWindow, type WindowShift } from '../../utils/dates.ts';

function setShips(next: string[]): void {
  modelShips.set(next);
  syncToUrl();
}

function setWindow(start: string, end: string, preset: DatePreset): void {
  modelStart.set(start);
  modelEnd.set(end);
  modelPreset.set(preset);
  syncToUrl();
}

export const modelSelection: SelectionScope = {
  kind: 'model',

  ships: () => modelShips.value,
  anchor: () => modelShips.value[0] ?? null,
  // SFOC compares ships; Service Power reads the first. One list serves both.
  multiAllowed: () => true,

  async pickOnly(id: string): Promise<void> {
    setShips([id]);
  },

  async addShip(id: string): Promise<void> {
    const ships = modelShips.value;
    if (ships.includes(id) || ships.length >= MAX_PLOT_SHIPS) return;
    setShips([...ships, id]);
  },

  async removeShip(id: string): Promise<void> {
    setShips(modelShips.value.filter((s) => s !== id));
  },

  start: () => modelStart.value,
  end: () => modelEnd.value,
  preset: () => modelPreset.value,

  applyPreset(preset: DatePreset): void {
    if (preset === 'custom') {
      modelPreset.set('custom');
      syncToUrl();
      return;
    }
    // `max` is "this ship's whole range", a metadata lookup for one ship;
    // `<date-range>` only offers it to Explore.
    const window = presetWindow(preset);
    if (window) setWindow(window.start, window.end, preset);
  },

  setStart(value: string): void {
    setWindow(value, modelEnd.value, 'custom');
  },

  setEnd(value: string): void {
    setWindow(modelStart.value, value, 'custom');
  },

  shift(shift: WindowShift): void {
    const moved = shiftWindow({ start: modelStart.value, end: modelEnd.value }, shift);
    if (moved) setWindow(moved.start, moved.end, 'custom');
  },

  // The pickers watch Explore's atoms, not these, so they are told here.
  subscribe(listener: () => void): () => void {
    const offs = [modelShips, modelStart, modelEnd, modelPreset].map((atom) =>
      atom.subscribe(() => listener()),
    );
    return () => offs.forEach((off) => off());
  },
};

let seeded = false;

/**
 * Start the Model Viewer from Explore's ships and window, once per page load:
 * the first time it is open with nothing picked and Explore has a ship. After
 * that the two are independent - emptying the selection here is a choice, not
 * something to undo by refilling it.
 *
 * Driven by `activeTab` and `selectedShip`, because either can arrive last - a
 * link restores the tab before the ship, a working view the other way round.
 */
export function seedFromExplore(): void {
  if (seeded || activeTab.value !== 'modelviewer') return;
  if (modelShips.value.length) {
    seeded = true;
    return;
  }
  const ships = plottedShipIds();
  if (!ships.length) return;
  seeded = true;
  modelShips.set(ships.slice(0, MAX_PLOT_SHIPS));
  // `max` is Explore's ship-metadata window; here it is just the dates it made.
  const preset = activePreset.value === 'max' ? 'custom' : activePreset.value;
  modelStart.set(startDate.value);
  modelEnd.set(endDate.value);
  modelPreset.set(preset);
  syncToUrl();
}

activeTab.subscribe(seedFromExplore);
selectedShip.subscribe(seedFromExplore);
